import { useMutation, useQuery } from '@tanstack/react-query';
import { Moment } from 'moment/moment';
import { getDatesRange, getTimezone } from '@mgm/common/utils';
import { IntegrationsController } from '../../../../services/integrations.api';
import { useAppDispatch, useAppSelector } from '../../../../hooks/useAppStore';
import { getAuthenticateUser } from '../../../../store/authenticate/selectors';
import { getMembers } from '../../../../store/team/selectors';
import { QUERY_KEYS } from '../../../../utils/enums';
import { payrollActions } from '../../../../store/payroll/slice';
import {
  IBaseResponse, TGetGustoDataRes, TGetGustoReportRes,
} from '../../../../types/responses';
import { TUpdateGustoDataReq } from '../../../../types/requests';
import { TGustoData } from '../../../../types/payroll.type';
import { useError } from '../../../../hooks/useCommon';

export function useGetGustoDataAPI() {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const members = useAppSelector(getMembers);
  const dispatch = useAppDispatch();
  const { handleReqError } = useError();

  return useQuery(
    [QUERY_KEYS.GUSTO],
    async () => {
      const data = {
        shopName: personnel.shopName,
      };
      const rsp = await IntegrationsController().getGustoData<TGetGustoDataRes>(data);

      if (rsp.success && 'integrationData' in rsp) {
        let gustoData: Array<TGustoData> = [];

        if (!rsp.integrationData?.length) {
          gustoData = members?.map((member, index) => ({
            id: String(index),
            email: member.email,
            shopName: member.shopName,
            name: member.name,
            avatarFile: member.avatarFile,
            ssn: '',
            gusto_employee_id: '',
            isNew: true,
          }));
        } else {
          gustoData = rsp.integrationData.map((integration, index) => {
            const userMatch = members.find((user) => user.email === integration.email);

            return {
              id: String(index),
              email: integration.email,
              shopName: userMatch ? userMatch.shopName : '',
              name: userMatch ? userMatch.name : '',
              avatarFile: userMatch ? userMatch.avatarFile : null,
              ssn: integration.ssn,
              gusto_employee_id: integration.gusto_employee_id,
              isNew: true,
            };
          });
        }

        dispatch(payrollActions.setGustoData(gustoData));
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, data);
      } else if ('msg' in rsp) {
        console.error(`ERROR! get gusto data failed! ${rsp.msg}`);
      }

      return rsp;
    },
    {
      refetchOnWindowFocus: false,
      refetchIntervalInBackground: false,
      refetchOnReconnect: false,
      suspense: false,
    },
  );
}

export function useUpdateGustoDataAPI() {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const { handleReqError } = useError();
  const updateGustoDataApi = (payload: Omit<TUpdateGustoDataReq, 'shopName'>) => {
    const data = {
      shopName: personnel.shopName,
      integrationData: payload.integrationData,
    };

    return IntegrationsController().updateGustoData<IBaseResponse>(data);
  };

  return useMutation(updateGustoDataApi, {
    onSuccess: (rsp, variables) => {
      if (rsp.success) {
        console.log('success');
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! update gusto data failed! ${rsp.msg}`);
      }
    },
    onError: (error) => {
      console.error(`ERROR! update gusto data request threw an Exception! ${error}`);
    },
  });
}

type TGetExportData = {
  dates: [Moment, Moment],
  emails: Array<string>
};

export function useGetGustoExportAPI() {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const dispatch = useAppDispatch();
  const { handleReqError } = useError();

  const getGustoReportApi = (payload: TGetExportData) => {
    const { fromDate, toDate } = getDatesRange(payload?.dates);

    const data = {
      ...payload,
      from: fromDate,
      to: toDate,
      shopName: personnel.shopName,
      timezone: getTimezone(),
    };

    return IntegrationsController().getGustoReport<TGetGustoReportRes>(data);
  };

  return useMutation(getGustoReportApi, {
    onSuccess: (rsp, variables) => {
      if (rsp?.success && 'report' in rsp) {
        const reports = rsp.report.map((report, index) => ({
          ...report,
          id: index,
          isNew: true,
        }));
        dispatch(payrollActions.setGustoReportData(reports));
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! get gusto report failed! ${rsp.msg}`);
      }
    },
    onError: (error) => {
      console.error(`ERROR! get gusto report threw an Exception! ${error}`);
    },
  });
}
